import React from 'react';
import { Badge, Tooltip, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { Link } from 'react-admin';
import { useHistory } from 'react-router-dom';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../../helpers/utils';
import { getFilterTextForChangeState, getTagForRuntimeStates } from '../../Common/Tags';
import { CounterpartyName, PartyField } from '../../Teams/Counterparty';
import { changeStateMeta } from './constants';

const { Text } = Typography;

/**
 * One value under the shared column header. `label` is not painted here — the
 * header owns it — but it stays in the DOM so the stacked narrow layout, where
 * there is no header, can print it back via CSS.
 */
const Cell = ({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) => (
    <Tooltip placement="topLeft" title={value || undefined}>
        <div className={`si-cell${value ? '' : ' si-cell--empty'}`} data-label={label}>
            {children ?? value ?? '—'}
        </div>
    </Tooltip>
);

export const ChangeState = ({ record, changeInstancesPath }: { record: any; changeInstancesPath: string }) => {
    const meta = changeStateMeta[record.change_state];
    if (!meta) return null;
    return (
        // the short form fits the row; the tooltip carries the full name
        <Tooltip title={`${meta.label} — view change instances`}>
            {/* stop propagation so this link wins over the card-wide click */}
            <Link
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                to={`${changeInstancesPath}?filter={"application_id":"${record.application?.id}","service_item_id":"${record.id}","state":"${getFilterTextForChangeState(record.change_state)}"}`}
            >
                <Badge color={meta.color} text={<Text type="secondary" className="text-sm">{meta.short}</Text>} />
            </Link>
        </Tooltip>
    );
};

const ServiceItemRow = ({
    record,
    showChangeState,
    clickable,
    detailPath,
    changeInstancesPath,
    partyField,
}: {
    record: any;
    showChangeState: boolean;
    clickable: boolean;
    detailPath: string;
    changeInstancesPath: string;
    partyField: PartyField;
}) => {
    const history = useHistory();
    const open = () => clickable && history.push(`${detailPath}/${record.id}`);
    const party = partyField.team(record);

    return (
        <div
            className={`si-row${clickable ? '' : ' si-row--static'}`}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? `Open service item ${record.name}` : undefined}
            onClick={open}
            onKeyDown={(e) => {
                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    open();
                }
            }}
        >
            {/* the number leads, then the name — the change instances list reads
                the same way */}
            <span className="si-id">#{record.id}</span>

            <Tooltip placement="topLeft" title={record.name}>
                <span className="si-name">{record.name}</span>
            </Tooltip>

            {/* whose it is, then what it belongs to, then what it is — and the
                whose opens the team behind the name */}
            <Cell label={partyField.label} value={party?.name}>
                <CounterpartyName team={party} pov={partyField.pov} label={partyField.label} />
            </Cell>
            <Cell label="Application" value={record.application?.name} />
            <Cell label="Service" value={record.service?.name} />

            <div className="si-state">
                {getTagForRuntimeStates(record.runtime_state)}
                {showChangeState && <ChangeState record={record} changeInstancesPath={changeInstancesPath} />}
            </div>

            <span className="si-created" data-label="Created">
                {record.created && (
                    <Tooltip title={`Created ${formatDateAndTime(record.created)}`}>
                        <span>{formatDateAndTimeFromNow(record.created)}</span>
                    </Tooltip>
                )}
            </span>

            <span className="si-row__chevron" aria-hidden="true">
                {clickable ? <RightOutlined /> : null}
            </span>
        </div>
    );
};

export default ServiceItemRow;
